import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarCheck } from "lucide-react";

const LINES = ["Relentless", "Advocacy.", "Landmark Results."];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1767311966535-5537c28a9ef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxsb3MlMjBhbmdlbGVzJTIwc2t5bGluZSUyMHdpbHNoaXJlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MzgwOTYwNXww&ixlib=rb-4.1.0&q=85"
          alt="Los Angeles skyline at dusk"
          className="h-[120%] w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/60 to-navy-950" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="mx-auto w-full max-w-7xl px-5 pt-32 pb-20 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="mb-6 font-mono text-xs tracking-[0.35em] text-gold/90 uppercase"
        >
          Heidari Law Group · Los Angeles · Sherman Oaks · Las Vegas
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-slate-50">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "italic text-gold" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.18, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
        >
          Over 21 years of fierce representation in Personal Injury, Employment Law, and Civil
          Rights across California &amp; Nevada. No fee unless we win.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={onBook}
            className="btn-sheen flex items-center gap-2 rounded-full bg-gradient-to-r from-[#D4AF37] via-[#C5A059] to-[#B38F32] px-7 py-3.5 text-sm font-bold text-navy-950 shadow-xl shadow-gold/25 transition-all hover:from-[#E6CA65] hover:to-[#D4AF37] active:scale-[0.98]"
          >
            <CalendarCheck size={17} /> Book Free Consultation
          </button>
          <a
            data-testid="hero-call-btn"
            href="tel:+14172831384"
            className="flex items-center gap-2 rounded-full border border-gold/40 px-7 py-3.5 text-sm font-semibold text-gold transition-all hover:border-gold hover:bg-gold/10 active:scale-[0.98]"
          >
            <Phone size={16} /> Call (417) 283-1384
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.35, duration: 0.7 }}
          className="mt-12 inline-flex items-center gap-3 rounded-full border border-gold/25 bg-navy-900/70 px-5 py-2.5 backdrop-blur-md"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-gold" />
          <span className="font-mono text-xs tracking-widest text-gold-champagne uppercase">
            $150,000,000+ Recovered for Clients
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
